import React, { useEffect, useState } from 'react';
import { getHistoricalData, syncHistorical } from '../../api';

export default function AdminApiData({
    races,
    teams,
    drivers,
    currentRace,
    onSyncRaces,
    onSyncTeams,
    onSyncDrivers,
}) {
    const [showAllRaces, setShowAllRaces] = useState(false);
    const [showAllTeams, setShowAllTeams] = useState(false);
    const [showAllDrivers, setShowAllDrivers] = useState(false);
    const [historicalRows, setHistoricalRows] = useState([]);
    const [historicalMessage, setHistoricalMessage] = useState('');

    useEffect(() => {
        const selectedRace = races.find((r) => String(r.id) === String(currentRace));
        const circuitId = selectedRace?.circuit_id;
        if (!circuitId) {
            setHistoricalRows([]);
            return;
        }

        getHistoricalData({
            circuitId: String(circuitId),
            fromSeason: 2023,
            toSeason: 2025,
        })
            .then((rows) => setHistoricalRows(Array.isArray(rows) ? rows : []))
            .catch(() => setHistoricalRows([]));
    }, [currentRace, races]);

    async function handleSyncHistorical() {
        if (!currentRace) {
            setHistoricalMessage('Select a race first');
            return;
        }
        try {
            const selectedRace = races.find((r) => String(r.id) === String(currentRace));
            const result = await syncHistorical(currentRace);
            const circuitId = result?.selectedRace?.circuitId || selectedRace?.circuit_id;
            if (!circuitId) {
                throw new Error('Selected race is missing circuit data');
            }
            const rows = await getHistoricalData({
                circuitId: String(circuitId),
                fromSeason: 2023,
                toSeason: 2025,
            });
            setHistoricalRows(Array.isArray(rows) ? rows : []);
            const skipped = (result.details || []).filter((x) => x.skipped).length;
            const synced = (result.details || []).filter((x) => !x.skipped).length;
            setHistoricalMessage(`Historical sync done (base 2026): ${synced} synced, ${skipped} already in DB`);
        } catch (e) {
            setHistoricalMessage(e.message || 'Historical sync failed');
        }
    }

    return (
        <>
            <h5 className="mb-3">API Data</h5>
            <div className="card mb-3">
                <div className="card-body">
                    <div className="mt-3 small text-muted">
                        Loaded races: {races.length} | Loaded teams: {teams.length} | Loaded drivers: {drivers.length}
                    </div>
                    <div className="accordion mt-2" id="apiDataAccordion">
                        <div className="accordion-item bg-transparent border-secondary">
                            <h2 className="accordion-header" id="headingRaces">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseRaces" aria-expanded="false" aria-controls="collapseRaces">
                                    Races ({races.length})
                                </button>
                            </h2>
                            <div id="collapseRaces" className="accordion-collapse collapse" aria-labelledby="headingRaces" data-bs-parent="#apiDataAccordion">
                                <div className="accordion-body">
                                    <div className="d-flex gap-2 mb-2">
                                        <button className="btn btn-outline-primary btn-sm" type="button" onClick={onSyncRaces}>Sync Races</button>
                                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setShowAllRaces((prev) => !prev)}>
                                            {showAllRaces ? 'Show less' : 'Show all'}
                                        </button>
                                    </div>
                                    {races.length > 0 && (
                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Round</th>
                                                        <th>Race</th>
                                                        <th>Date</th>
                                                        <th>Time (UTC)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(showAllRaces ? races : races.slice(0, 5)).map((r) => (
                                                        <tr key={r.id}>
                                                            <td>{r.round}</td>
                                                            <td>{r.name || r.race_name}</td>
                                                            <td>{r.date || r.race_date}</td>
                                                            <td>{r.race_time || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="accordion-item bg-transparent border-secondary">
                            <h2 className="accordion-header" id="headingTeams">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTeams" aria-expanded="false" aria-controls="collapseTeams">
                                    Teams ({teams.length})
                                </button>
                            </h2>
                            <div id="collapseTeams" className="accordion-collapse collapse" aria-labelledby="headingTeams" data-bs-parent="#apiDataAccordion">
                                <div className="accordion-body">
                                    <div className="d-flex gap-2 mb-2">
                                        <button className="btn btn-outline-primary btn-sm" type="button" onClick={onSyncTeams}>Sync Teams</button>
                                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setShowAllTeams((prev) => !prev)}>
                                            {showAllTeams ? 'Show less' : 'Show all'}
                                        </button>
                                    </div>
                                    {teams.length > 0 && (
                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Team</th>
                                                        <th>Short</th>
                                                        <th>Country</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(showAllTeams ? teams : teams.slice(0, 5)).map((t) => (
                                                        <tr key={t.id}>
                                                            <td>{t.name}</td>
                                                            <td>{t.short_name}</td>
                                                            <td>{t.country}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="accordion-item bg-transparent border-secondary">
                            <h2 className="accordion-header" id="headingDrivers">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseDrivers" aria-expanded="false" aria-controls="collapseDrivers">
                                    Drivers ({drivers.length})
                                </button>
                            </h2>
                            <div id="collapseDrivers" className="accordion-collapse collapse" aria-labelledby="headingDrivers" data-bs-parent="#apiDataAccordion">
                                <div className="accordion-body">
                                    <div className="d-flex gap-2 mb-2">
                                        <button className="btn btn-outline-primary btn-sm" type="button" onClick={onSyncDrivers}>Sync Drivers</button>
                                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setShowAllDrivers((prev) => !prev)}>
                                            {showAllDrivers ? 'Show less' : 'Show all'}
                                        </button>
                                    </div>
                                    {drivers.length > 0 && (
                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Code</th>
                                                        <th>Driver</th>
                                                        <th>Team</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(showAllDrivers ? drivers : drivers.slice(0, 5)).map((d) => (
                                                        <tr key={d.id}>
                                                            <td>{d.code}</td>
                                                            <td>{d.first_name} {d.last_name}</td>
                                                            <td>{d.team_short_name || d.team_name || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="accordion-item bg-transparent border-secondary">
                            <h2 className="accordion-header" id="headingHistorical">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseHistorical" aria-expanded="false" aria-controls="collapseHistorical">
                                    Historical (previous 3 years for selected race)
                                </button>
                            </h2>
                            <div id="collapseHistorical" className="accordion-collapse collapse" aria-labelledby="headingHistorical" data-bs-parent="#apiDataAccordion">
                                <div className="accordion-body">
                                    <div className="d-flex flex-wrap gap-2 mb-2 align-items-end">
                                        <div className="small text-muted">
                                            Historical data for current race
                                        </div>
                                        {/* {historicalRows.length === 0 && ( */}
                                            <button className="btn btn-outline-primary btn-sm" type="button" onClick={handleSyncHistorical}>Sync Historical (Selected Race)</button>
                                        {/* )} */}
                                    </div>
                                    {historicalMessage && <div className="small text-muted mb-2">{historicalMessage}</div>}
                                    {historicalRows.length > 0 && (
                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Season</th>
                                                        <th>Round</th>
                                                        <th>Race</th>
                                                        <th>Results</th>
                                                        <th>Qualifying</th>
                                                        <th>Sprint</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historicalRows.map((h) => (
                                                        <tr key={h.id}>
                                                            <td>{h.season}</td>
                                                            <td>{h.round ?? '—'}</td>
                                                            <td>{h.race_name || '—'}</td>
                                                            <td>{h.results_count ?? 0}</td>
                                                            <td>{h.qualifying_count ?? 0}</td>
                                                            <td>{h.sprint_count ?? 0}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
